import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { TrackingModule } from '@freelancer/tracking';
import { trackByTitleProperty, trackByValue } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import type { ColumnNumberDesktopLarge } from '@freelancer/ui/column';
import { ContainerSize } from '@freelancer/ui/container';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { PictureLayout } from '@freelancer/ui/picture';
import { UiModule } from '@freelancer/ui/ui.module';
import type {
  MultipleDescriptionTitle,
  TitleDescription,
} from '../landing-components.types';
import {
  InfoColumnsTitle2Directive,
  InfoColumnsTitleDirective,
} from '../landing-page-slots';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    InfoColumnsTitleDirective,
    InfoColumnsTitle2Directive,
    TrackingModule,
    UiModule,
  ],
  selector: 'fl-info-columns',
  template: `
    <fl-picture
      alt=""
      class="Banner-image"
      [layout]="PictureLayout.RESPONSIVE"
      [height]="imageHeight"
      [width]="imageWidth"
      [src]="imageSrc"
    >
    </fl-picture>

    @if (addShadowBg) {
      <div class="Banner-shadow"></div>
    }

    <fl-container
      class="Top"
      [size]="ContainerSize.DESKTOP_XLARGE"
    >
      <p
        class="BarlowLineHeight font-barlow-condensed uppercase text-light text-xxlarge tablet:text-xxxlarge desktop:text-marketing_mega text-center font-black whitespace-pre-line mb-large desktop:mb-xxlarge"
      >
        @if (customTitle) {
          <ng-content select="[flInfoColumnsTitle]"></ng-content>
        } @else {
          {{ titleText }}
        }
      </p>

      <div class="TitleDescription mb-large desktop:mb-xxxlarge">
        <fl-grid>
          @for (
            titleDescription of titleDescriptions;
            track trackByTitleProperty(i, titleDescription);
            let i = $index
          ) {
            <fl-col
              [col]="12"
              [colTablet]="6"
              class="{{
                i < titleDescriptions.length - 1 ? 'mb-mid' : 'mb-none'
              }}"
            >
              <div class="TitleDescription-item">
                <fl-picture
                  alt=""
                  class="TitleDescription-icon-picture"
                  [src]="'back-to-work-landing-page/ui-check-brush.png'"
                  [boundedWidth]="true"
                ></fl-picture>
                <div class="TitleDescription-content">
                  <p
                    class="TitleDescription-title text-light text-mid tablet:text-large desktop:text-xlarge font-bold mb-xxsmall whitespace-pre-line"
                  >
                    {{ titleDescription.title }}
                  </p>
                  <p
                    class="text-light text-body-16 tablet:text-body-16 desktop:text-body-16 whitespace-pre-line"
                  >
                    {{ titleDescription.description }}
                  </p>
                </div>
              </div>
            </fl-col>
          }
        </fl-grid>
      </div>

      <p
        class="BarlowLineHeight font-barlow-condensed uppercase text-light text-xxlarge tablet:text-xxxlarge desktop:text-marketing_mega text-center font-black whitespace-pre-line mb-large desktop:mb-xxlarge"
      >
        @if (customTitle2) {
          <ng-content select="[flInfoColumnsTitle2]"></ng-content>
        } @else {
          {{ titleText2 }}
        }
      </p>

      <fl-grid
        class="mb-small desktop:mb-xxlarge"
        [vAlign]="VerticalAlignment.VERTICAL_STRETCH"
      >
        @for (
          multipleDescriptionTitle of multipleDescriptionTitles;
          track trackByTitleProperty(i, multipleDescriptionTitle);
          let i = $index
        ) {
          <fl-col
            class="mb-small"
            [col]="12"
            [colDesktopLarge]="getColDesktopLarge"
            [colTablet]="6"
          >
            <div class="TitleDescription-multiple">
              <div class="TitleDescription-header">
                <span class="TitleDescription-number text-light font-black">{{
                  (i + 1).toString().padStart(2, '0')
                }}</span>
                <p
                  class="TitleDescription-title text-light text-body-16 tablet:text-mid desktop:text-large font-bold whitespace-pre-line"
                >
                  {{ multipleDescriptionTitle.title }}
                </p>
              </div>
              <ul [class.Bulletpoints]="!hideBulletPoints">
                @for (
                  description of multipleDescriptionTitle.descriptions;
                  track trackByValue($index, description)
                ) {
                  <li [class.Bullet]="!hideBulletPoints">
                    <p
                      class="text-light text-body-16 tablet:text-body-16 desktop:text-body-16 whitespace-pre-line"
                    >
                      {{ description }}
                    </p>
                  </li>
                }
              </ul>
            </div>
          </fl-col>
        }
      </fl-grid>

      <div class="Button">
        <fl-button
          flTrackingLabel="ctaText"
          [color]="ButtonColor.PRIMARY"
          [size]="ButtonSize.XLARGE"
          [link]="ctaLink"
          (click)="handleCtaTrigger()"
        >
          {{ ctaText }}
        </fl-button>
      </div>
    </fl-container>
  `,
  styleUrls: ['./info-columns.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoColumnsComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ContainerSize = ContainerSize;
  PictureLayout = PictureLayout;
  VerticalAlignment = VerticalAlignment;
  trackByTitleProperty = trackByTitleProperty;
  trackByValue = trackByValue;

  @ContentChild(InfoColumnsTitleDirective)
  customTitle: InfoColumnsTitleDirective;
  @ContentChild(InfoColumnsTitle2Directive)
  customTitle2: InfoColumnsTitle2Directive;

  @Input() addShadowBg? = false;
  @Input() ctaText: string;
  @Input() ctaLink?: string;
  @Input() imageSrc: string;
  @Input() imageHeight: number;
  @Input() imageWidth: number;
  @Input() multipleDescriptionTitles: readonly MultipleDescriptionTitle[];
  @Input() hideBulletPoints?: boolean;
  @Input() titleText?: string;
  @Input() titleText2?: string;
  @Input() titleDescriptions: readonly TitleDescription[];
  @Output() ctaTrigger = new EventEmitter<void>();

  @HostBinding('style.--image-width')
  get cssImageWidth(): number {
    return this.imageWidth;
  }

  @HostBinding('style.--image-height')
  get cssImageHeight(): number {
    return this.imageHeight;
  }

  get getColDesktopLarge(): ColumnNumberDesktopLarge {
    return Math.floor(
      12 / this.multipleDescriptionTitles.length,
    ) as ColumnNumberDesktopLarge;
  }

  handleCtaTrigger(): void {
    if (this.ctaLink === undefined) {
      this.ctaTrigger.emit();
    }
  }
}
